import {
  ACCEPTANCE_CRITERIA_OPTION,
  ShiftPostStep,
} from '@features/shift-post-v2/steps'
import {
  PositionNameSection,
  ShiftIdentifierSection,
} from '@features/shift-post-v2/steps/shift-details-step/components'
import { LocationSection } from '@features/shift-post-v2/steps/shift-details-step/components'
import { UpshiftersNumberSection } from '@features/shift-post-v2/steps/shift-details-step/components'
import { ScheduleSection } from '@features/shift-post-v2/steps/shift-details-step/components'
import { WhoCanWorkSection } from '@features/shift-post-v2/steps/acceptance-criteria-step/components'
import { InviteUpshiftersSection } from '@features/shift-post-v2/steps/invite-upshifters-step/components'
import { ScreeningSection } from '@features/shift-post-v2/steps/screening-requirements-step/components'
import AuthService from '@src/helpers/authService'
import { useMemo } from 'react'
import { FEATURE_TOGGLE_MULTIDAY_REVAMP } from '@src/config/featureToggles'
import { SHIFT_PROGRESS, SHIFT_TYPE } from '@src/models/Shift'
import { connect } from 'formik'
import {
  checkIfBusinessRequiresNextWorkingDaySelection,
  IN_PROGRESS_MULTIDAY_SHIFT_ALERT_MESSAGE,
  IN_PROGRESS_MULTIDAY_SHIFT_NO_EDIT_ALERT_MESSAGE,
} from '@src/helpers/shiftHelpers'
import { Alert } from '@pinchenterprisesnpm/friday-ui'
import { getSelectedAcceptanceCriteriaOption } from '@src/features/shift-post-v2/utils'
import { ShiftPostPage } from '@src/features/shift-post-v2/pages'
import { IConfirmStepProps } from './confirm-step.types'
import { EstimatedTotalSection } from './components'
import { ConfirmStepContainer } from './confirm-step.styles'

const ConfirmStepComponent = ({
  sourcePage,
  shouldRenderAutoAcceptCertificates = true,
  formik,
}: IConfirmStepProps) => {
  const values = formik?.values

  const businessEntity = AuthService.getBusinessEntity()
  const isRostersEnabled = businessEntity?.business_setting?.rosters_enabled
  const hasAutoAcceptNowButShiftDidNot =
    businessEntity?.auto_accept && !values?.auto_accept

  const selectedAcceptanceCriteriaOption = useMemo(
    () =>
      getSelectedAcceptanceCriteriaOption(
        values?.auto_accept_by_rosters ?? false,
        values?.auto_accept ?? false,
        hasAutoAcceptNowButShiftDidNot ?? false,
      ),
    [
      values?.auto_accept_by_rosters,
      values?.auto_accept,
      hasAutoAcceptNowButShiftDidNot,
    ],
  )

  const isAutoAccept =
    selectedAcceptanceCriteriaOption !==
    ACCEPTANCE_CRITERIA_OPTION.PERSONALLY_APPROVED

  const isEditInProgressMultidayShift =
    Number(values?.type) === SHIFT_TYPE.MULTIDAY &&
    Number(values?.progress) === SHIFT_PROGRESS.ACTIVE &&
    FEATURE_TOGGLE_MULTIDAY_REVAMP &&
    sourcePage === ShiftPostPage.EDIT_SHIFT

  const activeMultidayBanner = useMemo(() => {
    if (isEditInProgressMultidayShift) {
      const message = checkIfBusinessRequiresNextWorkingDaySelection()
        ? IN_PROGRESS_MULTIDAY_SHIFT_ALERT_MESSAGE
        : IN_PROGRESS_MULTIDAY_SHIFT_NO_EDIT_ALERT_MESSAGE
      return <Alert message={message} type="warning" />
    }
    return null
  }, [isEditInProgressMultidayShift])

  const hideScheduleSection = useMemo(() => {
    return (
      isEditInProgressMultidayShift &&
      !checkIfBusinessRequiresNextWorkingDaySelection()
    )
  }, [isEditInProgressMultidayShift])

  return (
    <ConfirmStepContainer>
      {isEditInProgressMultidayShift ? null : (
        <>
          <PositionNameSection
            sourceStep={ShiftPostStep.CONFIRM}
            sourcePage={sourcePage}
          />
          <ShiftIdentifierSection
            sourceStep={ShiftPostStep.CONFIRM}
            sourcePage={sourcePage}
          />
          <LocationSection
            sourceStep={ShiftPostStep.CONFIRM}
            sourcePage={sourcePage}
          />
          <UpshiftersNumberSection
            sourceStep={ShiftPostStep.CONFIRM}
            sourcePage={sourcePage}
          />
        </>
      )}
      {activeMultidayBanner}
      {hideScheduleSection ? null : (
        <ScheduleSection
          sourceStep={ShiftPostStep.CONFIRM}
          sourcePage={sourcePage}
        />
      )}
      {isEditInProgressMultidayShift ? null : (
        <WhoCanWorkSection
          sourceStep={ShiftPostStep.CONFIRM}
          sourcePage={sourcePage}
        />
      )}
      {shouldRenderAutoAcceptCertificates &&
        isAutoAccept &&
        !isEditInProgressMultidayShift && (
          <ScreeningSection
            sourceStep={ShiftPostStep.CONFIRM}
            sourcePage={sourcePage}
          />
        )}
      {isRostersEnabled && !isEditInProgressMultidayShift && (
        <InviteUpshiftersSection
          sourceStep={ShiftPostStep.CONFIRM}
          sourcePage={sourcePage}
        />
      )}
      <EstimatedTotalSection
        sourceStep={ShiftPostStep.CONFIRM}
        sourcePage={sourcePage}
      />
    </ConfirmStepContainer>
  )
}

export const ConfirmStep = connect<IConfirmStepProps>(ConfirmStepComponent)
